import { FC } from "react";
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SxProps } from "@mui/system";
import { CommitmentsMetadataCardChipButton } from "@/components/pages/commtiments/common/components/metadata-card/CommitmentsMetadataCardChipButton";
import { CommitmentType } from "@/services/cloudchipr.api";

export interface CommitmentsMetadataCardChipData {
  reservedInstances: {
    value: string;
    tooltipTitle: string;
  };
  savingsPlans: {
    value: string;
    tooltipTitle: string;
  };
  onClick: (type: CommitmentType) => void;
}

interface CommitmentsPageHeaderMetadataCardProps {
  primary: string;
  value: string;
  loading: boolean;
  valueColor?: string;
  valueUnit?: string;
  tooltip?: TooltipProps["title"];
  sx?: SxProps<Theme>;
  descriptionText?: string;
  chipData?: CommitmentsMetadataCardChipData;
}

export const CommitmentsPageHeaderMetadataCard: FC<
  CommitmentsPageHeaderMetadataCardProps
> = ({
  value,
  valueColor,
  valueUnit,
  loading,
  primary,
  tooltip,
  sx,
  descriptionText,
  chipData,
}) => {
  return (
    <Stack spacing={0.5} sx={{ p: 1.5, ...sx }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {primary}
        </Typography>

        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "action.disabled" }} />
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography
          mt={0.5}
          variant="h6"
          fontWeight="medium"
          color={valueColor}
        >
          {loading ? loadingSkeleton : value}
        </Typography>
        {valueUnit && <Typography variant="subtitle1">/{valueUnit}</Typography>}
      </Stack>

      {chipData && (
        <Chip
          size="small"
          sx={{ ...chipStyles, bgcolor: "#3F51B514" }}
          label={
            <Stack direction="row" alignItems="center">
              <CommitmentsMetadataCardChipButton
                type="reservation"
                onClick={chipData.onClick}
                value={chipData.reservedInstances.value}
                tooltipTitle={chipData.reservedInstances.tooltipTitle}
              />

              <Box sx={dotStyles} />

              <CommitmentsMetadataCardChipButton
                type="savings_plan"
                onClick={chipData.onClick}
                value={chipData.savingsPlans.value}
                tooltipTitle={chipData.savingsPlans.tooltipTitle}
              />
            </Stack>
          }
        />
      )}

      {descriptionText && (
        <Chip
          size="small"
          sx={chipStyles}
          label={
            <Typography variant="caption" color="text.secondary">
              {descriptionText}
            </Typography>
          }
        />
      )}
    </Stack>
  );
};

const loadingSkeleton = <Skeleton variant="text" height={32} width={120} />;

const chipStyles = {
  alignSelf: "flex-start",
  borderRadius: 1,
  overflow: "hidden",
  bgcolor: "grey.100",
};

const dotStyles = {
  width: 4,
  height: 4,
  borderRadius: "50%",
  bgcolor: "grey.300",
};
